// ============================================================================
// ACTIVITY LOG SERVICE
// ----------------------------------------------------------------------------
// Tenant activity belongs to the authenticated backend. This service is the
// only frontend seam for reading and writing it, and deliberately has no
// localStorage fallback: a browser-local list cannot answer "who did what" for
// the business and must never be presented as the tenant audit authority.
// ============================================================================

import { apiFetch } from './data-service-api';

export type ActivityLogAction =
    | 'create'
    | 'save'
    | 'update'
    | 'delete'
    | 'export'
    | 'import'
    | 'settings'
    | 'ai'
    | 'system'
    | (string & {});

export interface ActivityLogDetails {
    type?: string;
    entityType?: string;
    entityId?: string | null;
    description?: string;
    metadata?: unknown;
    [key: string]: unknown;
}

export interface ActivityLogEntry {
    id: string;
    timestamp: string | null;
    action: string;
    entityType: string | null;
    entityId: string | null;
    description: string;
    metadata: unknown;
    userId: string | null;
    actorName: string | null;
    actorEmail: string | null;
}

interface ActivityLogApiRow {
    id?: unknown;
    timestamp?: unknown;
    createdAt?: unknown;
    created_at?: unknown;
    action?: unknown;
    entityType?: unknown;
    entity_type?: unknown;
    entityId?: unknown;
    entity_id?: unknown;
    description?: unknown;
    details?: unknown;
    metadata?: unknown;
    userId?: unknown;
    user_id?: unknown;
    actorName?: unknown;
    actor_name?: unknown;
    actorEmail?: unknown;
    actor_email?: unknown;
}

export interface GetActivityLogOptions {
    action?: string;
    entityType?: string;
    limit?: number;
    signal?: AbortSignal;
}

export type ActivityLogCursor = string;

export interface ActivityLogPage {
    entries: ActivityLogEntry[];
    hasMore: boolean;
    nextCursor: ActivityLogCursor | null;
}

export interface GetActivityLogPageOptions extends GetActivityLogOptions {
    cursor?: ActivityLogCursor | null;
}

const DEFAULT_LIMIT = 500;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function optionalString(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed || null;
}

function normaliseTimestamp(value: unknown): string | null {
    if (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date)) return null;
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

/** Convert camelCase API rows (and defensive snake_case fixtures) into one UI contract. */
export function normaliseActivityLogRow(row: ActivityLogApiRow): ActivityLogEntry | null {
    if (!row || typeof row !== 'object') return null;
    const id = optionalString(row.id);
    const action = optionalString(row.action);
    if (!id || !action) return null;

    const details = row.details && typeof row.details === 'object'
        ? row.details as Record<string, unknown>
        : null;
    const description = optionalString(row.description)
        || optionalString(details?.description)
        || '';
    const metadata = row.metadata !== undefined
        ? row.metadata
        : details
            ? Object.fromEntries(Object.entries(details).filter(([key]) => key !== 'description'))
            : null;

    return {
        id,
        timestamp: normaliseTimestamp(row.createdAt ?? row.created_at ?? row.timestamp),
        action,
        entityType: optionalString(row.entityType ?? row.entity_type ?? details?.type),
        entityId: optionalString(row.entityId ?? row.entity_id),
        description,
        metadata,
        userId: optionalString(row.userId ?? row.user_id),
        actorName: optionalString(row.actorName ?? row.actor_name),
        actorEmail: optionalString(row.actorEmail ?? row.actor_email),
    };
}

function boundedLimit(value: unknown): number {
    const parsed = Number.parseInt(String(value ?? DEFAULT_LIMIT), 10);
    if (!Number.isFinite(parsed)) return DEFAULT_LIMIT;
    return Math.max(1, Math.min(DEFAULT_LIMIT, parsed));
}

function readCursor(value: unknown): ActivityLogCursor | null {
    if (typeof value !== 'string') return null;
    const cursor = value.trim();
    return cursor && cursor.length <= 512 ? cursor : null;
}

function readOptions(options: GetActivityLogOptions) {
    const params = new URLSearchParams();
    params.set('limit', String(boundedLimit(options.limit)));
    const action = optionalString(options.action);
    const entityType = optionalString(options.entityType);
    if (action) params.set('action', action);
    if (entityType) params.set('entityType', entityType);
    return params;
}

function writePayload(action: ActivityLogAction, details: ActivityLogDetails | string) {
    const objectDetails: ActivityLogDetails = typeof details === 'string'
        ? { description: details }
        : (details || {});
    const entityType = optionalString(objectDetails.entityType ?? objectDetails.type);
    const candidateEntityId = optionalString(objectDetails.entityId);
    const description = optionalString(objectDetails.description);
    const suppliedMetadata = objectDetails.metadata;
    const remainingMetadata = Object.fromEntries(
        Object.entries(objectDetails).filter(([key, value]) => (
            !['entityType', 'entityId', 'type', 'description', 'metadata'].includes(key)
            && value !== undefined
        )),
    );
    const metadata = suppliedMetadata !== undefined
        ? suppliedMetadata
        : Object.keys(remainingMetadata).length > 0
            ? remainingMetadata
            : null;

    return {
        action: String(action || 'system'),
        entityType,
        // activity_log.entity_id is UUID. Do not turn a natural key into a 500.
        entityId: candidateEntityId && UUID_RE.test(candidateEntityId) ? candidateEntityId : null,
        description,
        metadata,
    };
}

export const ActivityLogService = {
    /**
     * Record an activity after the owning operation succeeds. Audit transport is
     * best-effort for callers: it reports failure but never breaks their flow.
     */
    async log(action: ActivityLogAction, details: ActivityLogDetails | string): Promise<boolean> {
        try {
            await apiFetch('/activity-log', {
                method: 'POST',
                body: JSON.stringify(writePayload(action, details)),
            });
            return true;
        } catch (error: any) {
            console.warn('[activity-log] write failed:', error?.message || error);
            return false;
        }
    },

    async getAll(options: GetActivityLogOptions = {}): Promise<ActivityLogEntry[]> {
        const params = readOptions(options);

        const rows = await apiFetch(`/activity-log?${params.toString()}`, {
            signal: options.signal,
        });
        if (!Array.isArray(rows)) throw new TypeError('Activity log response was not a list.');
        return rows.map(normaliseActivityLogRow).filter((row): row is ActivityLogEntry => row !== null);
    },

    async getPage(options: GetActivityLogPageOptions = {}): Promise<ActivityLogPage> {
        const params = readOptions(options);
        if (options.cursor) {
            params.set('cursor', options.cursor);
        }
        const response = await apiFetch(`/activity-log/page?${params.toString()}`, {
            signal: options.signal,
        });
        if (!response || typeof response !== 'object' || !Array.isArray(response.items)) {
            throw new TypeError('Activity log page response was invalid.');
        }
        const hasMore = response.hasMore === true;
        const nextCursor = readCursor(response.nextCursor);
        if (hasMore && !nextCursor) throw new TypeError('Activity log page cursor was invalid.');
        return {
            entries: response.items
                .map(normaliseActivityLogRow)
                .filter((row: ActivityLogEntry | null): row is ActivityLogEntry => row !== null),
            hasMore,
            nextCursor,
        };
    },
};
